import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Card, CardBody, Input, Label, Spinner } from 'reactstrap'
import { toast } from 'react-toastify'
import { Btn, H3 } from '../../../AbstractElements'
import { postJson } from '../../../hook/request'
import { createLabel } from './createLabel'
import { getPrinterSerial, PrinterPort, writePrintJob } from './printer'

type PreparedLabel = ReturnType<typeof createLabel>
type ScanResult = { codice: string; message: string; color: 'success' | 'warning' | 'danger' }

export default function EtichetteScanner() {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const portRef = useRef<PrinterPort | null>(null)
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  const [connected, setConnected] = useState(false)
  const [authorizedPort, setAuthorizedPort] = useState<PrinterPort | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [codice, setCodice] = useState('')
  const [label, setLabel] = useState<PreparedLabel | null>(null)
  const [result, setResult] = useState<ScanResult | null>(null)
  const supported = window.isSecureContext && !!getPrinterSerial()

  useEffect(() => {
    mountedRef.current = true
    const serial = getPrinterSerial()
    if (window.isSecureContext && serial) {
      void serial.getPorts().then(ports => {
        // Non scegliere automaticamente tra più dispositivi autorizzati.
        if (mountedRef.current && ports.length === 1) setAuthorizedPort(ports[0])
      }).catch(() => {})
    }
    return () => {
      mountedRef.current = false
      const port = portRef.current
      portRef.current = null
      if (port) void port.close().catch(() => {})
    }
  }, [])

  const connect = async (previousPort?: PrinterPort) => {
    const serial = getPrinterSerial()
    if (!serial || !window.isSecureContext || busyRef.current) return
    busyRef.current = true
    setConnecting(true)
    setConnectionError(null)
    let selected: PrinterPort | null = null
    try {
      // La selezione deve partire direttamente dal tocco sul pulsante.
      selected = previousPort ?? await serial.requestPort()
      await selected.open({ baudRate: 9600 })
      if (!mountedRef.current) {
        await selected.close()
        return
      }
      portRef.current = selected
      setAuthorizedPort(selected)
      const port = selected
      port.addEventListener('disconnect', () => {
        if (portRef.current === port) {
          portRef.current = null
          if (mountedRef.current) {
            setConnected(false)
            toast.warn('Stampante disconnessa. Riconnettila prima di scansionare.')
          }
        }
      })
      setConnected(true)
      toast.success('Stampante connessa')
    } catch (err: any) {
      if (selected) void selected.close().catch(() => {})
      if (mountedRef.current) {
        const message = err?.name === 'NotFoundError'
          ? 'Nessun dispositivo selezionato. Se la stampante non compare, controlla i passaggi qui sotto.'
          : err?.message || 'Connessione alla stampante non riuscita'
        setConnectionError(message)
        if (err?.name !== 'NotFoundError') toast.error(message)
      }
    } finally {
      busyRef.current = false
      if (mountedRef.current) {
        setConnecting(false)
        inputRef.current?.focus()
      }
    }
  }

  const disconnect = async () => {
    if (busyRef.current) return
    busyRef.current = true
    setConnecting(true)
    const port = portRef.current
    portRef.current = null
    setConnected(false)
    try {
      if (port) await port.close()
    } catch {
      toast.warn('Connessione chiusa. Ricollega la stampante se necessario.')
    } finally {
      busyRef.current = false
      setConnecting(false)
    }
  }

  const sendLabel = useCallback(async (prepared: PreparedLabel) => {
    const port = portRef.current
    if (!port?.writable) {
      portRef.current = null
      setConnected(false)
      if (port) void port.close().catch(() => {})
      throw new Error('Connetti la stampante prima di stampare')
    }
    try {
      await writePrintJob(port, prepared.commands)
      if (portRef.current !== port) throw new Error('Stampante disconnessa durante la stampa')
      setResult({ codice: prepared.data.codice, message: 'Etichetta inviata alla stampante', color: 'success' })
    } catch {
      if (portRef.current === port) {
        portRef.current = null
        setConnected(false)
      }
      void port.close().catch(() => {})
      // Nessun tentativo automatico: il dispositivo potrebbe aver già stampato parte del lavoro.
      throw new Error('Invio interrotto. Verifica la carta: la stampa potrebbe essere parziale. Riconnetti e ristampa solo se necessario.')
    }
  }, [])

  const invia = useCallback(async (raw: string) => {
    const code = raw.trim()
    if (busyRef.current) return
    if (!code) { toast.warn('Scansiona un barcode'); return }
    if (!portRef.current?.writable) {
      setConnected(false)
      toast.warn('Connetti la stampante prima di scansionare')
      return
    }
    busyRef.current = true
    setLoading(true)
    setResult(null)
    setLabel(null)
    try {
      const res = await postJson<any>('/etichette/create', { codice: code })
      const payload = res.data
      if (String(payload?.status ?? '').toLowerCase() !== 'ok') {
        throw new Error(String(payload?.message ?? payload?.esito ?? 'Impossibile creare la etichetta'))
      }
      const prepared = createLabel(payload)
      setLabel(prepared)
      setResult({ codice: prepared.data.codice, message: 'Invio alla stampante...', color: 'warning' })
      await sendLabel(prepared)
    } catch (err: any) {
      const message = String(err?.response?.data?.message ?? err?.message ?? 'Errore di rete')
      setResult({ codice: code, message, color: 'danger' })
      toast.error(message)
    } finally {
      setCodice('')
      busyRef.current = false
      setLoading(false)
      inputRef.current?.focus()
    }
  }, [sendLabel])

  const reprint = async () => {
    if (!label || busyRef.current) return
    busyRef.current = true
    setLoading(true)
    try {
      await sendLabel(label)
    } catch (err: any) {
      setResult({ codice: label.data.codice, message: err.message, color: 'danger' })
      toast.error(err.message)
    } finally {
      busyRef.current = false
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  useEffect(() => {
    if (connected && !connecting && !loading) inputRef.current?.focus()
  }, [connected, connecting, loading])

  useEffect(() => {
    if (codice.trim().length <= 9 || !connected) return
    const timeout = setTimeout(() => { void invia(codice) }, 250)
    return () => clearTimeout(timeout)
  }, [codice, connected, invia])

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <H3 className="mb-1">Scanner Etichette</H3>
          <p className="text-muted">Connetti la stampante, poi scansiona il codice per stampare l'etichetta.</p>
          {!supported && (
            <Alert color="warning">
              {!window.isSecureContext
                ? 'Apri il sito in HTTPS per collegare la stampante.'
                : 'La stampa diretta non è disponibile in questo browser. Usa Chrome aggiornato su Android.'}
            </Alert>
          )}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Btn color={connected ? 'secondary' : 'primary'} type="button"
              disabled={!supported || loading || connecting}
              onClick={() => { if (connected) void disconnect(); else void connect() }}>
              {connecting ? <Spinner size="sm" /> : connected ? 'Disconnetti stampante' : 'Collega stampante al sito'}
            </Btn>
            <span className={connected ? 'text-success' : 'text-muted'}>
              {connected ? 'Stampante connessa' : 'Sito non collegato alla stampante'}
            </span>
          </div>
          {!connected && supported && (
            <div className="mb-3">
              <p className="text-muted">
                La stampante può essere già associata ad Android: questo sito deve comunque
                ricevere l'autorizzazione da Chrome e aprire la propria connessione.
              </p>
              {authorizedPort && (
                <Btn color="secondary" type="button" className="mb-2" disabled={connecting || loading}
                  onClick={() => { void connect(authorizedPort) }}>
                  Ricollega stampante già autorizzata
                </Btn>
              )}
              {connectionError && <Alert color="warning">{connectionError}</Alert>}
              <details>
                <summary>La stampante non compare?</summary>
                <ol className="mt-2 mb-0">
                  <li>Apri la pagina direttamente in Chrome aggiornato, non nel browser interno di un'altra app.</li>
                  <li>Nelle impostazioni Android verifica che Chrome abbia il permesso “Dispositivi nelle vicinanze”, se presente.</li>
                  <li>Lascia la stampante associata nelle impostazioni Bluetooth. Scollegala da Printer Easy e dalle altre app che la stanno usando.</li>
                  <li>Spegni e riaccendi la stampante, poi premi “Collega stampante al sito” e autorizza il dispositivo.</li>
                </ol>
                <p className="text-muted mt-2 mb-0">
                  Chrome mostra solo i dispositivi con una porta Bluetooth seriale compatibile.
                  Una stampa riuscita da Printer Easy non conferma da sola la compatibilità con Chrome.
                </p>
              </details>
            </div>
          )}
          {result && (
            <Alert color={result.color}>
              <strong>Codice:</strong> {result.codice}<br />
              <strong>Esito:</strong> {result.message}
            </Alert>
          )}
          {label && (
            <div className="mb-3">
              <img src={label.preview} alt="Anteprima etichetta saldi"
                style={{ width: 180, maxWidth: '100%', height: 'auto' }} />
              <small className="text-muted d-block mt-1">Formato etichetta: 72 × 120 mm · Spazio per lo strappo: 20 mm</small>
              <Btn color="secondary" type="button" className="mt-2"
                disabled={!connected || loading || connecting} onClick={reprint}>
                Ristampa etichetta
              </Btn>
            </div>
          )}
          <form onSubmit={e => { e.preventDefault(); void invia(codice) }}>
            <Label htmlFor="etichette-barcode">Barcode</Label>
            <Input id="etichette-barcode" innerRef={inputRef} value={codice}
              onChange={e => setCodice(e.target.value)} placeholder="Scansiona qui..."
              autoComplete="off" disabled={!connected} readOnly={loading || connecting} />
            <Btn color="primary" type="submit" className="mt-2"
              disabled={!connected || loading || connecting || !codice.trim()}>
              {loading ? <><Spinner size="sm" /> Invio in corso...</> : 'Stampa etichetta'}
            </Btn>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}

